/**
 * Workflows Page
 */

import { isAdminRole } from '../utils/role';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Topbar } from '../components/layout';
import { Button, Badge, Modal, SearchInput, Pagination, useToast } from '../components/ui';
import { workflowApi, Workflow, WorkflowRunStatus } from '../api';
import { authApi } from '../api';
import RefreshIcon from '../assets/icons/common/refresh.svg?react';
import CheckIcon from '../assets/icons/common/check.svg?react';
import CloseIcon from '../assets/icons/common/close.svg?react';
import SkipIcon from '../assets/icons/common/skip.svg?react';
// 작업 컬럼 아이콘 (사용자관리 · 메뉴관리 테이블과 동일)
import EditIcon from '../assets/icons/common/edit.svg?react';
import TrashIcon from '../assets/icons/common/trash.svg?react';
import PauseIcon from '../assets/icons/common/pause.svg?react';
import PlayIcon from '../assets/icons/common/play.svg?react';
// 종류 컬럼 아이콘 - 챗봇 · 스케줄 · 메일은 워크플로우 빌더 트리거 노드와 같은 선 아이콘 (assets/icons/flow)
// 웹훅 · 파일 · 자동화는 assets/icons/workflow-type
// - 일반 아이콘: svg 컴포넌트(?react) → 색은 CSS(.wf-type-icon = primary)로 통일
// - 서비스 로고(Gmail · Drive · Notion · 네이버 · Slack): 원래 색 유지 위해 <img>
import ChatbotTypeIcon from '../assets/icons/flow/chat.svg?react';
import ScheduleTypeIcon from '../assets/icons/flow/schedule.svg?react';
import ManualRunTypeIcon from '../assets/icons/flow/manual-run.svg?react';
import MailTypeIcon from '../assets/icons/flow/mail-trigger.svg?react';
import WebhookTypeIcon from '../assets/icons/workflow-type/webhook.svg?react';
import FileTypeIcon from '../assets/icons/workflow-type/file.svg?react';
import AutomationTypeIcon from '../assets/icons/workflow-type/automation.svg?react';
import notionLogo from '../assets/icons/marketplace/notion.svg';
import googleDriveLogo from '../assets/icons/marketplace/google-drive.svg';
import gmailLogo from '../assets/icons/marketplace/gmail.svg';
import naverLogo from '../assets/icons/marketplace/naver.svg';
import slackLogo from '../assets/icons/marketplace/slack.svg';
import SortArrowsIcon from '../assets/icons/common/sort.svg?react';
// 아이콘 색 - 워크플로우 빌더 노드와 같은 색 (FlowSteps NODE_STEPS tone → --flow-*)
import { NODE_STEPS, type FlowTone } from '../components/workflow/FlowSteps';

// 워크플로우 종류 (트리거 노드 type → 아이콘 · 문구) - icon: svg 컴포넌트 / logo: 로고 이미지 / tone: 노드 색
interface WfType {
  label: string;
  icon?: React.FC<React.SVGProps<SVGSVGElement>>;
  logo?: string;
  tone?: FlowTone;
}
const WF_TYPE: Record<string, WfType> = {
  'chatbot':          { icon: ChatbotTypeIcon,    label: '챗봇',   tone: NODE_STEPS['trigger'].tone },
  'schedule-trigger': { icon: ScheduleTypeIcon,   label: '스케줄', tone: NODE_STEPS['schedule-trigger'].tone },
  'mail-trigger':     { icon: MailTypeIcon,       label: '메일',   tone: NODE_STEPS['mail-trigger'].tone },
  'webhook-trigger':  { icon: WebhookTypeIcon,    label: '웹훅' },
  'file-trigger':     { icon: FileTypeIcon,       label: '파일' },
  'manual-trigger':   { icon: ManualRunTypeIcon,  label: '수동 실행', tone: NODE_STEPS['manual-trigger'].tone },
  'notion':           { logo: notionLogo,         label: 'Notion' },
  'google-drive':     { logo: googleDriveLogo,    label: 'Drive' },
  'gmail':            { logo: gmailLogo,          label: 'Gmail' },
  'naver':            { logo: naverLogo,          label: '네이버' },
  'slack':            { logo: slackLogo,          label: 'Slack' },
  'automation':       { icon: AutomationTypeIcon, label: '자동화' },
};

const renderWfTypeIcon = (type: WfType) => {
  if (type.logo) return <img className="wf-type-icon wf-type-logo" src={type.logo} alt="" />;
  if (type.icon) {
    const Icon = type.icon;
    return <Icon className={`wf-type-icon${type.tone ? ` wf-type-icon--${type.tone}` : ''}`} />;
  }
  return null;
};

// 마지막 실행 상태 → 동그라미 안 아이콘 (성공 ✓ / 실패 × / 스킵 »)
const RUN_STATUS_ICON: Record<WorkflowRunStatus, React.FC<React.SVGProps<SVGSVGElement>>> = {
  success: CheckIcon,
  failed: CloseIcon,
  skipped: SkipIcon,
};
import '../styles/workflow.css';


export function WorkflowsPage() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  // workflowId → 마지막 실행 상태
  const [lastRunMap, setLastRunMap] = useState<Record<string, { status: WorkflowRunStatus; startedAt: string }>>({});
  const [activeScheduleIds, setActiveScheduleIds] = useState<Set<string>>(new Set());
  const [scheduleActionId, setScheduleActionId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortKey, setSortKey] = useState<'name' | 'description' | 'type' | 'lastRun' | 'isSharedAsTemplate' | 'isSharedAsChatbot' | 'ownerName' | 'createdAt' | 'updatedAt'>('createdAt');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const PAGE_SIZE = 10;

  // 수동 실행 모달 상태 (제거됨 - 바로 실행)
  const [running, setRunning] = useState(false);

  const handleSort = (key: typeof sortKey) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
    setCurrentPage(1);
  };

  // 정렬 아이콘 (↓↑) - 정렬 중인 칸은 primary 색 + 방향 화살표만 진하게 (styles/workflow.css .table-sort-icon)
  const SortIcon = ({ col }: { col: typeof sortKey }) => (
    <span className={`table-sort-icon${sortKey === col ? ` active ${sortDir}` : ''}`}>
      <SortArrowsIcon />
    </span>
  );

  const currentUser = authApi.getUser();
  const isAdmin = isAdminRole(currentUser?.role);

  // 수동 실행
  const handleRun = async (workflow: Workflow) => {
    try {
      showToast('info', `"${workflow.name}" 실행 중...`);
      const result = await workflowApi.run(workflow.id);
      if (result.status === 'success') {
        showToast('success', `"${workflow.name}" 실행 완료`);
      } else if (result.status === 'skipped') {
        showToast('info', `"${workflow.name}" 건너뜀 — 처리할 새 항목이 없습니다.`);
      } else {
        showToast('error', `실행 실패: ${result.errorMessage || '알 수 없는 오류'}`);
      }
      // 실행 로그 갱신
      loadWorkflows();
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message;
      showToast('error', `오류: ${msg || '실행에 실패했습니다.'}`);
    }
  };

  useEffect(() => {
    loadWorkflows();
  }, []);

  const loadWorkflows = async () => {
    try {
      setLoading(true);
      const data = await workflowApi.list();
      setWorkflows(data);

      // 모든 워크플로우의 마지막 실행 로그 병렬 조회
      const entries = await Promise.all(
        data.map(async (wf) => {
          try {
            const logs = await workflowApi.getLogs(wf.id, 1);
            if (logs.length > 0) {
              return [wf.id, { status: logs[0].status, startedAt: logs[0].startedAt }] as const;
            }
          } catch {
            // 조회 실패 무시
          }
          return null;
        })
      );
      const map: Record<string, { status: WorkflowRunStatus; startedAt: string }> = {};
      entries.forEach((e) => { if (e) map[e[0]] = e[1]; });
      setLastRunMap(map);

      // 활성 스케줄 조회
      try {
        const ids = await workflowApi.getActiveSchedules();
        setActiveScheduleIds(new Set(ids));
      } catch {
        // 무시
      }
    } catch (error) {
      showToast('error', '워크플로우 목록을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 검색 변경 시 1페이지로 초기화
  useEffect(() => { setCurrentPage(1); }, [searchQuery]);

  // 스케줄 트리거 타입
  const SCHEDULE_TRIGGER = 'schedule-trigger';

  // 검색 필터링 + 정렬
  const filteredWorkflows = workflows
    .filter((wf) => {
      if (!searchQuery.trim()) return true;
      const query = searchQuery.toLowerCase();

      // 워크플로우 종류: 스케줄 아니면 챗봇
      const triggerNode = wf.nodes?.find(n => n.type === SCHEDULE_TRIGGER);
      const typeLabel = triggerNode ? WF_TYPE['schedule-trigger'].label
        : wf.nodes?.some(n => n.type === 'manual-trigger') ? WF_TYPE['manual-trigger'].label
        : WF_TYPE.chatbot.label;

      return (
        (wf.name || '').toLowerCase().includes(query) ||
        (wf.description || '').toLowerCase().includes(query) ||
        (wf.ownerName || '').toLowerCase().includes(query) ||
        typeLabel.toLowerCase().includes(query) ||
        (wf.isSharedAsTemplate ? '템플릿 공유됨' : '').includes(query) ||
        (wf.isSharedAsChatbot ? '챗봇 공유됨' : '').includes(query) ||
        (wf.createdAt || '').includes(query)
      );
    })
  .sort((a, b) => {
    let av = '', bv = '';
    if (sortKey === 'name') { av = a.name || ''; bv = b.name || ''; }
    else if (sortKey === 'description') { av = a.description || ''; bv = b.description || ''; }
    else if (sortKey === 'type') {
      const getType = (wf: typeof a) => {
        const t = wf.nodes?.find(n => ['trigger','schedule-trigger','mail-trigger','webhook-trigger'].includes(n.type));
        return t ? t.type : 'trigger';
      };
      av = getType(a); bv = getType(b);
    }
    else if (sortKey === 'lastRun') {
      av = lastRunMap[a.id]?.startedAt || ''; bv = lastRunMap[b.id]?.startedAt || '';
    }
    else if (sortKey === 'ownerName') { av = a.ownerName || ''; bv = b.ownerName || ''; }
    else if (sortKey === 'isSharedAsTemplate') {
      av = a.isSharedAsTemplate ? '1' : '0'; bv = b.isSharedAsTemplate ? '1' : '0';
    }
    else if (sortKey === 'isSharedAsChatbot') {
      av = a.isSharedAsChatbot ? '1' : '0'; bv = b.isSharedAsChatbot ? '1' : '0';
    }
    else if (sortKey === 'createdAt') { av = a.createdAt || ''; bv = b.createdAt || ''; }
    else if (sortKey === 'updatedAt') { av = a.updatedAt || ''; bv = b.updatedAt || ''; }
    return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
  });

  // 페이징
  const totalPages = Math.ceil(filteredWorkflows.length / PAGE_SIZE);
  const pagedWorkflows = filteredWorkflows.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  // 빈 화면으로 이동만 (DB 저장은 저장 버튼 클릭 시)
  const handleCreateNew = () => {
    navigate('/workflows/new');
  };

  const handleDelete = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setDeleteConfirmId(id);
  };

  const confirmDelete = async () => {
    const id = deleteConfirmId;
    if (!id) return;
    setDeleteConfirmId(null);
    try {
      setDeletingId(id);
      await workflowApi.delete(id);
      setWorkflows((prev) => prev.filter((w) => w.id !== id));
      showToast('success', '워크플로우가 삭제되었습니다.');
    } catch (error) {
      showToast('error', '워크플로우 삭제에 실패했습니다.');
    } finally {
      setDeletingId(null);
    }
  };

  const handleSchedulePause = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setScheduleActionId(id);
    try {
      await workflowApi.pauseSchedule(id);
      setActiveScheduleIds(prev => { const next = new Set(prev); next.delete(id); return next; });
      showToast('success', '스케줄이 정지됐습니다.');
    } catch {
      showToast('error', '정지에 실패했습니다.');
    } finally {
      setScheduleActionId(null);
    }
  };

  const handleScheduleResume = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setScheduleActionId(id);
    try {
      await workflowApi.resumeSchedule(id);
      setActiveScheduleIds(prev => new Set([...prev, id]));
      showToast('success', '스케줄이 재가동됐습니다.');
    } catch {
      showToast('error', '재가동에 실패했습니다.');
    } finally {
      setScheduleActionId(null);
    }
  };

  // 검색어 하이라이트 함수
  const highlightText = (text: string) => {
    if (!searchQuery.trim() || !text) return text;
    
    const query = searchQuery.toLowerCase();
    const lowerText = text.toLowerCase();
    const index = lowerText.indexOf(query);
    
    if (index === -1) return text;
    
    const before = text.slice(0, index);
    const match = text.slice(index, index + query.length);
    const after = text.slice(index + query.length);
    
    return (
      <>
        {before}
        <span className="search-highlight">{match}</span>
        {after}
      </>
    );
  };

  return (
    <>
      <Topbar
        title="워크플로우"
        description="RAG 챗봇 워크플로우를 생성하고 관리합니다"
        actions={
          <Button onClick={handleCreateNew} size='md'>
            + 새 워크플로우
          </Button>
        }
      />

      <div className="page-content">
        <div className="page-content-inner">
          {/* 검색 입력 */}
          {workflows.length > 0 && (
            <div className="wf-search-bar">
              <SearchInput
                placeholder="이름, 설명, 종류, 만든 사람으로 검색..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onClear={() => setSearchQuery('')}
              />
              <button
                type="button"
                className="wf-search-refresh"
                onClick={loadWorkflows}
                disabled={loading}
                title="새로고침"
                aria-label="새로고침"
              >
                <RefreshIcon />
              </button>
            </div>
          )}

          {loading ? (
            <div className="loading">로딩 중...</div>
          ) : workflows.length === 0 ? (
            <div className="empty-state">
              <p>아직 워크플로우가 없습니다.</p>
              <p>새 워크플로우를 생성해보세요.</p>
            </div>
          ) : filteredWorkflows.length === 0 ? (
            <div className="empty-state">
              <p>"{searchQuery}"에 대한 검색 결과가 없습니다.</p>
            </div>
          ) : (
            <div className='table-wrap'>
              <table>
                <thead>
                  <tr>
                    <th style={{ cursor: 'pointer' }} onClick={() => handleSort('name')}>이름 <SortIcon col="name" /></th>
                    <th style={{ cursor: 'pointer' }} onClick={() => handleSort('description')}>설명 <SortIcon col="description" /></th>
                    <th style={{ cursor: 'pointer' }} onClick={() => handleSort('type')}>종류 <SortIcon col="type" /></th>
                    <th style={{ cursor: 'pointer' }} onClick={() => handleSort('lastRun')}>마지막 실행 <SortIcon col="lastRun" /></th>
                    <th style={{ cursor: 'pointer' }} onClick={() => handleSort('isSharedAsTemplate')}>템플릿 <SortIcon col="isSharedAsTemplate" /></th>
                    <th style={{ cursor: 'pointer' }} onClick={() => handleSort('isSharedAsChatbot')}>챗봇 <SortIcon col="isSharedAsChatbot" /></th>
                    <th style={{ cursor: 'pointer' }} onClick={() => handleSort('ownerName')}>만든 사람 <SortIcon col="ownerName" /></th>
                    <th style={{ cursor: 'pointer' }} onClick={() => handleSort('createdAt')}>생성일 <SortIcon col="createdAt" /></th>
                    <th>작업</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedWorkflows.map((workflow) => (
                    <tr key={workflow.id}>
                      <td className="left" title={workflow.name}>
                        <span
                          className="wf-name-link"
                          onClick={() => navigate(`/workflows/${workflow.id}`)}
                        >
                          {highlightText(workflow.name)}
                        </span>
                      </td>
                      <td className="left" title={workflow.description || ''}>
                        {workflow.description ? highlightText(workflow.description) : <span className="wf-empty">-</span>}
                      </td>
                      <td>
                        {(() => {
                          const triggerNode = workflow.nodes.find(n =>
                            ['trigger', 'schedule-trigger', 'mail-trigger', 'manual-trigger', 'webhook-trigger', 'file-trigger',
                            'notion', 'google-drive', 'gmail', 'naver', 'slack'].includes(n.type)
                          );
                          // 스케줄 트리거인 경우
                          if (triggerNode?.type === 'schedule-trigger') {
                            const isScheduleActive = activeScheduleIds.has(workflow.id);
                            return (
                              <div className='wf-type-badges'>
                                <span
                                  className={`wf-schedule-dot ${isScheduleActive ? 'active' : 'inactive'}`}
                                  title={isScheduleActive ? '스케줄러 실행 중' : '스케줄러 미등록'}
                                />
                                <span className="wf-type-badge trigger">
                                  {renderWfTypeIcon(WF_TYPE['schedule-trigger'])}{WF_TYPE['schedule-trigger'].label}
                                </span>
                              </div>
                            );
                          }
                          // 그 외는 모두 챗봇으로 표시
                          // 수동 실행 트리거인 경우
                          if (triggerNode?.type === 'manual-trigger') {
                            return (
                              <span className="wf-type-badge manual">
                                {renderWfTypeIcon(WF_TYPE['manual-trigger'])}{WF_TYPE['manual-trigger'].label}
                              </span>
                            );
                          }
                          return (
                            <span className="wf-type-badge chatbot">
                              {renderWfTypeIcon(WF_TYPE.chatbot)}{WF_TYPE.chatbot.label}
                            </span>
                          );
                        })()}
                      </td>
                      <td>
                        {(() => {
                          const run = lastRunMap[workflow.id];
                          if (!run) return <span className="wf-empty">-</span>;
                          const statusLabel: Record<WorkflowRunStatus, string> = {
                            success: '성공',
                            failed: '실패',
                            skipped: '스킵',
                          };
                          const StatusIcon = RUN_STATUS_ICON[run.status];
                          return (
                            <span title={new Date(run.startedAt).toLocaleString('ko-KR')}
                              className={`wf-run-status ${run.status}`}>
                              <span className="wf-run-status-icon"><StatusIcon /></span>
                              {statusLabel[run.status]}
                            </span>
                          );
                        })()}
                      </td>
                      <td>
                        {workflow.isSharedAsTemplate
                          ? <Badge variant="type" shape="tag">공유됨</Badge>
                          : <span className="wf-empty">-</span>}
                      </td>
                      <td>
                        {workflow.isSharedAsChatbot
                          ? <Badge variant="type" shape="tag">공유됨</Badge>
                          : <span className="wf-empty">-</span>}
                      </td>
                      <td title={workflow.ownerName || ''}>{workflow.ownerName || <span className="wf-empty">-</span>}</td>
                      <td>{new Date(workflow.createdAt).toLocaleDateString()}</td>
                      <td>
                        <div className="wf-actions">
                          {/* 스케줄 정지/재가동: 관리자이거나 본인 소유만 */}
                          {(isAdmin || workflow.owner === currentUser?.email) && (() => {
                            const isSchedule = workflow.nodes.some(n => n.type === 'schedule-trigger');
                            const isActive = activeScheduleIds.has(workflow.id);
                            const isActing = scheduleActionId === workflow.id;
                            if (isSchedule) {
                              return isActive ? (
                                <button className="icon-action-btn" onClick={(e) => handleSchedulePause(workflow.id, e)} disabled={isActing} title="스케줄 정지"><PauseIcon /></button>
                              ) : (
                                <button className="icon-action-btn" onClick={(e) => handleScheduleResume(workflow.id, e)} disabled={isActing} title="스케줄 재가동"><PlayIcon /></button>
                              );
                            }
                            return null;
                          })()}
                          {/* 수동 실행: manual-trigger 워크플로우만 표시 */}
                          {workflow.nodes?.some(n => n.type === 'manual-trigger') && (
                            <button className="icon-action-btn" onClick={(e) => { e.stopPropagation(); handleRun(workflow); }} title="실행">
                              <PlayIcon />
                            </button>
                          )}
                          {/* 편집: 관리자이거나 본인 소유만 */}
                          {(isAdmin || workflow.owner === currentUser?.email) && (
                            <button className="icon-action-btn" onClick={() => navigate(`/workflows/${workflow.id}`)} title="편집">
                              <EditIcon />
                            </button>
                          )}
                          {/* 삭제: 관리자이거나 본인 소유만 */}
                          {(isAdmin || workflow.owner === currentUser?.email) && (
                            <button className="icon-action-btn delete" onClick={(e) => handleDelete(workflow.id, e)} disabled={deletingId !== null} title="삭제">
                              <TrashIcon />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* 페이지네이션 */}
          {!loading && totalPages > 1 && (
            <Pagination
              current={currentPage}
              total={totalPages}
              onChange={setCurrentPage}
              info={<><b>{(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, filteredWorkflows.length)}</b> / {filteredWorkflows.length}개</>}
            />
          )}
        </div>
      </div>

      {/* 워크플로우 삭제 확인 모달 */}
      <Modal
        isOpen={!!deleteConfirmId}
        onClose={() => setDeleteConfirmId(null)}
        title="워크플로우 삭제"
        size="sm"
        style={{ maxWidth: 360 }}
        bodyClassName="chat-confirm-body"
        footerLeft={
          <Button color="gray" appearance="outline" onClick={() => setDeleteConfirmId(null)}>취소</Button>
        }
        footerRight={
          <Button color="error" onClick={confirmDelete}>삭제</Button>
        }
      >
          워크플로우를 삭제하시겠습니까?<br />삭제 후 복구할 수 없습니다.
      </Modal>

    </>
  );
}

export default WorkflowsPage;
